import { Dialog, Listbox, Transition } from '@headlessui/react'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { XIcon } from '@heroicons/react/solid'
import clsx from 'clsx'
import ExtLink from 'components/common/ExtLink'
import Button from 'components/forms/Button'
import CheckButton from 'components/forms/CheckButton'
import ResetButton from 'components/forms/ResetButton'
import TextInput from 'components/forms/TextInput'
import TextInputWithLabel from 'components/forms/TextInputWithLabel'
import ChevronDownIcon from 'components/icons/ChevronDownIcon'
import ChevronUpIcon from 'components/icons/ChevronUpIcon'
import SmileIcon from 'components/icons/SmileIcon'
import { APP_ORIGIN, PHONE_PLACEHOLDER } from 'config/constants'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import useBaseForm from 'hooks/useBaseForm'
import useSlug from 'hooks/useSlug'
import { inProduction } from 'lib/misc'
import { isDiff, isEmptyString, isNonEmptyString } from 'lib/utils'
import {
  Email,
  MaskedPhoneNumber,
  NonEmptySubMaxString,
} from 'lib/validator'
import { Fragment, useCallback, useState } from 'react'
import { Controller, FormProvider } from 'react-hook-form'
import MaskedInput from 'react-input-mask'
import { useSendServiceFormMutation } from 'store/api'
import { z } from 'zod'
export const serviceSchema = z.object({
  date: NonEmptySubMaxString,
  name: NonEmptySubMaxString,
  phone: MaskedPhoneNumber,
  email: Email,
  persons: NonEmptySubMaxString,
  comment: z.string(),
  budget: z.string(),
  type: NonEmptySubMaxString,
  agreed: z.boolean(),
  page: z.string(),
})
dayjs.extend(localizedFormat)

const types = [
  'Фуршет',
  'Кофе брейк',
  'Welcome зона',
  'Шведский стол',
  'Банкет',
  'Гала-Ужин',
  'BBQ',
  'Сладкий стол',
]

const ServiceForm = ({
  inCatering = false,
}: {
  inCatering?: boolean
}) => {
  const { router } = useSlug()

  const [sendServiceForm, { isLoading: isSendServiceFormLoading }] =
    useSendServiceFormMutation()

  const handleSubmit = useCallback(
    async (data: z.infer<typeof serviceSchema>) => {
      const date = dayjs(data.date).locale('ru').format('LL')
      const updatedData = { ...data, date, page: router.asPath }
      await sendServiceForm({ payload: updatedData, lng: 'ru' })
        .unwrap()
        .then(() => {
          formCtx.reset()
          if (typeof window !== 'undefined' && inProduction) {
            window.ym!(16650304, 'reachGoal', 'form')
          }
          setIsOpen(true)
          return null
        })
    },
    [sendServiceForm, router.asPath]
  )
  const { formCtx, formIsSubmitting, submitForm } = useBaseForm(
    handleSubmit,
    serviceSchema,
    {
      date: '',
      name: '',
      phone: '',
      email: '',
      persons: '',
      comment: '',
      budget: '',
      type: '',
      agreed: false,
      page: '',
    },
    'onSubmit'
  )
  const phoneError = formCtx.formState.errors.phone?.message
  const typeError = formCtx.formState.errors.type?.message
  const phone = formCtx.watch('phone')
  const type = formCtx.watch('type')
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <FormProvider {...formCtx}>
        <div className='font-unbounded font-bold uppercase mb-4'>
          Оформить заявку
        </div>
        <div
          className={clsx('flex gap-4', {
            'flex-col lg:flex-row': inCatering,
            'flex-col': !inCatering,
          })}
        >
          <div
            className={clsx(
              ' flex flex-col md:grid md:auto-rows-max md:grid-cols-12 gap-4 w-full',
              {
                'lg:w-2/3': inCatering,
                'w-full': !inCatering,
              }
            )}
          >
            <div className='col-span-4'>
              <TextInputWithLabel
                name='name'
                placeholder='Ваше имя *'
                className='bg-white'
              />
            </div>
            <div className='col-span-4'>
              <TextInputWithLabel
                name='persons'
                placeholder='Кол-во персон'
                type='number'
                className='bg-white'
              />
            </div>
            <div className='col-span-4'>
              <Listbox
                value={type}
                onChange={(x) => {
                  formCtx.setValue('type', x)
                  formCtx.clearErrors('type')
                }}
              >
                {({ open }) => (
                  <>
                    <div className='relative'>
                      <Listbox.Button
                        className={clsx(
                          'bg-white relative w-full rounded-md text-bodyGrey tracking-tight shadow-sm pl-5 pr-10 py-2.5 text-left cursor-pointer focus:outline-none',
                          {
                            'form-error': isNonEmptyString(typeError),
                          }
                        )}
                      >
                        <span
                          className={clsx('block truncate', {
                            'text-darkGrey': isEmptyString(type),
                          })}
                        >
                          {type || 'Формат мероприятия'}
                        </span>
                        <span className='absolute inset-y-0 right-0 flex items-center pr-3.5 pl-2 pointer-events-none'>
                          {open ? (
                            <ChevronUpIcon
                              className='h-6 w-6 text-darkGrey'
                              aria-hidden='true'
                            />
                          ) : (
                            <ChevronDownIcon
                              className='h-6 w-6 text-darkGrey'
                              aria-hidden='true'
                            />
                          )}
                        </span>
                      </Listbox.Button>
                      <Transition
                        show={open}
                        as={Fragment}
                        leave='transition ease-in duration-100'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                      >
                        <Listbox.Options className='absolute z-30 mt-1 w-full bg-white shadow-lg max-h-60 py-1.5 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none'>
                          {types.map((x) => (
                            <Listbox.Option
                              key={x}
                              className={({ active }) =>
                                clsx(
                                  'font-medium cursor-pointer select-none relative py-2.5 pl-5 pr-10 leading-snug text-sm',
                                  {
                                    'bg-lightYellow text-dark':
                                      active,
                                    'text-bodyGrey': !active,
                                  }
                                )
                              }
                              value={x}
                            >
                              <span className='block'>{x}</span>
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
              {isNonEmptyString(typeError) && (
                <div className='flex items-center gap-1 text-warning text-xs'>
                  <ExclamationCircleIcon className='flex-shrink-0 w-4 h-4 text-warning' />
                  Выберите мероприятие
                </div>
              )}
            </div>
            <Controller
              name='phone'
              control={formCtx.control}
              defaultValue=''
              render={({ field }) => (
                <MaskedInput
                  mask='+7 (999) 999-99-99'
                  // @ts-ignore
                  maskChar='_'
                  value={field.value}
                  onChange={field.onChange}
                >
                  {(inputProps: any) => (
                    <div className='relative col-span-4'>
                      <input
                        id='phone'
                        className={clsx(
                          'form-input rounded-md w-full pt-4 pb-1 pl-5 pr-10 tracking-tight bg-white',
                          {
                            'form-error':
                              isNonEmptyString(phoneError),
                            'form-disabled': formIsSubmitting,
                          }
                        )}
                        disabled={formIsSubmitting}
                        {...inputProps}
                        placeholder={PHONE_PLACEHOLDER}
                        type='tel'
                      />
                      <label
                        htmlFor='phone'
                        className='absolute text-2xs text-bodyGrey top-1 left-5.5'
                      >
                        Ваш телефон
                      </label>
                      <ResetButton
                        condition={
                          !formIsSubmitting &&
                          isNonEmptyString(phone) &&
                          isDiff(phone, PHONE_PLACEHOLDER)
                        }
                        onClick={() => formCtx.setValue('phone', '')}
                        className='right-4 top-3.5'
                      />
                      {isNonEmptyString(phoneError) && (
                        <div className='mb-3.5 flex items-start lg:mt-1 mt-1.5'>
                          <ExclamationCircleIcon className='flex-shrink-0 w-4 h-4 text-warning' />
                          <div className='ml-1 text-xs leading-tight text-warning'>
                            {phoneError}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </MaskedInput>
              )}
            />
            <div className='col-span-4'>
              <TextInputWithLabel
                name='budget'
                placeholder='Бюджет на персону'
                type='number'
                className='bg-white'
              />
            </div>
            <div className='col-span-4 row-span-2'>
              <TextInput
                textArea
                name='comment'
                placeholder='Комментарий'
                className='bg-white form-textarea py-2.5 px-5 w-full leading-snug'
                rows={4}
                withResetButton={false}
              />
            </div>
            <div className='col-span-4'>
              <TextInputWithLabel
                name='email'
                placeholder='Ваша почта *'
                className='bg-white'
              />
            </div>
            <div className='col-span-4'>
              <TextInputWithLabel
                withResetButton={false}
                name='date'
                type='date'
                min={new Date().toISOString().split('T')[0]}
                placeholder='Дата мероприятия'
                className='bg-white pr-5'
              />
            </div>
            <div className='col-span-8 flex items-center md:justify-end pr-2'>
              <CheckButton
                checked={formCtx.watch('agreed')}
                onClick={() => {
                  formCtx.setValue('agreed', !formCtx.watch('agreed'))
                }}
              ></CheckButton>
              <span className='ml-2.5 -mt-px text-xs tracking-tight leading-snug lg:leading-snug select-none text-bodyGrey'>
                <ExtLink
                  className='font-semibold hover:underline text-dark'
                  href={`${APP_ORIGIN}/personal/`}
                >
                  Лицензионное соглашение
                </ExtLink>
                {' на обработку данных'}
              </span>
            </div>
            <div className='col-span-4'>
              <Button
                disabled={!formCtx.watch('agreed')}
                loading={isSendServiceFormLoading}
                loadingClasses='h-4 w-4 mx-1.5 bg-accent'
                onClick={submitForm}
                className='w-full btn-primary text-sm font-medium tracking-tight py-2.5 text-center relative flex-shrink-0'
              >
                Отправить заявку
              </Button>
            </div>
          </div>

        </div>

      </FormProvider>

      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className='fixed z-60 inset-0 overflow-y-auto'
          onClose={setIsOpen}
        >
          <div className='flex items-end justify-center min-h-screen text-center sm:block'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Dialog.Overlay className='fixed inset-0 bg-dark bg-opacity-75 transition-opacity' />
            </Transition.Child>
            <span
              className='hidden sm:inline-block sm:align-middle sm:h-screen'
              aria-hidden='true'
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <div className='inline-block align-bottom bg-white px-4 md:px-6 lg:px-8 xl:px-16 pt-8 pb-10 shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:px-6 sm:py-10 w-full'>
                <button
                  aria-label='Закрыть'
                  type='button'
                  className='absolute -top-8 right-1 md:top-0 md:-right-8 focus:outline-none'
                  onClick={() => setIsOpen(false)}
                >
                  <XIcon
                    className='h-6 w-6 text-dark'
                    aria-hidden='true'
                  />
                </button>
                <div className='w-full mx-auto p-5 flex flex-col gap-y-2 justify-center text-center'>
                  <SmileIcon className='mx-auto text-[#00C78D]' />
                  <span className='text-2xl font-semibold'>
                    Ваша заявка отправлена!
                  </span>
                  <span className='text-lg text-dark'>
                    Мы свяжемся с Вами в ближайшее время.
                  </span>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
export default ServiceForm
